import React, {
  CSSProperties,
  RefCallback,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { Popper } from '@mui/material';
import { getPositioner } from '@remirror/extension-positioner';
import {
  useActive,
  useAttrs,
  useChainedCommands,
  useCurrentSelection,
  useExtensionEvent,
  usePositioner,
  useUpdateReason
} from '@remirror/react';
import { CommandButton, FloatingToolbar } from '@remirror/react-ui';
import {
  createMarkPositioner,
  LinkExtension,
  ShortcutHandlerProps
} from 'remirror/extensions';

function useLinkShortcut() {
  const [linkShortcut, setLinkShortcut] = useState<
    ShortcutHandlerProps | undefined
  >();
  const [isEditing, setIsEditing] = useState(false);

  useExtensionEvent(
    LinkExtension,
    'onShortcut',
    useCallback(
      (props) => {
        if (!isEditing) {
          setIsEditing(true);
        }

        return setLinkShortcut(props);
      },
      [isEditing]
    )
  );

  return { linkShortcut, isEditing, setIsEditing };
}

function useFloatingLinkState() {
  const chain = useChainedCommands();
  const { isEditing, linkShortcut, setIsEditing } = useLinkShortcut();
  const { to, empty } = useCurrentSelection();

  const url = (useAttrs().link()?.href as string) ?? '';
  const [href, setHref] = useState<string>(url);

  const linkPositioner = useMemo(
    () => createMarkPositioner({ type: 'link' }),
    []
  );

  const onRemove = useCallback(() => chain.removeLink().focus().run(), [chain]);

  const updateReason = useUpdateReason();

  useLayoutEffect(() => {
    if (!isEditing) {
      return;
    }

    if (updateReason.doc || updateReason.selection) {
      setIsEditing(false);
    }
  }, [isEditing, setIsEditing, updateReason.doc, updateReason.selection]);

  useEffect(() => {
    setHref(url);
  }, [url]);

  const submitHref = useCallback(() => {
    setIsEditing(false);
    const range = linkShortcut ?? undefined;

    if (href === '') {
      chain.removeLink();
    } else {
      chain.updateLink({ href, auto: false }, range);
    }

    chain.focus(range?.to ?? to).run();
  }, [setIsEditing, linkShortcut, chain, href, to]);

  const cancelHref = useCallback(() => {
    setIsEditing(false);
  }, [setIsEditing]);

  const clickEdit = useCallback(() => {
    if (empty) {
      chain.selectLink();
    }

    setIsEditing(true);
  }, [chain, empty, setIsEditing]);

  return useMemo(
    () => ({
      href,
      setHref,
      linkShortcut,
      linkPositioner,
      isEditing,
      setIsEditing,
      clickEdit,
      onRemove,
      submitHref,
      cancelHref
    }),
    [
      href,
      linkShortcut,
      linkPositioner,
      isEditing,
      clickEdit,
      onRemove,
      submitHref,
      cancelHref
    ]
  );
}

export const FloatingLinkToolbar: React.FC<{
  isDialogOpen: boolean;
  setIsDialogOpen: (open: boolean) => void;
}> = ({ isDialogOpen, setIsDialogOpen }) => {
  const { onRemove, isEditing, setIsEditing } = useFloatingLinkState();
  const active = useActive();
  const activeLink = active.link();

  useEffect(() => {
    setIsEditing(isDialogOpen);
  }, [isDialogOpen]);

  useEffect(() => {
    setIsDialogOpen(isEditing);
  }, [isEditing]);

  const handleClickEdit = useCallback(() => {
    setIsEditing(true);
  }, []);

  const linkEditButtons = activeLink ? (
    <>
      <CommandButton
        commandName="updateLink"
        onSelect={handleClickEdit}
        icon="pencilLine"
        enabled
      />
      <CommandButton
        commandName="removeLink"
        onSelect={onRemove}
        icon="linkUnlink"
        enabled
      />
    </>
  ) : (
    <CommandButton
      commandName="updateLink"
      onSelect={handleClickEdit}
      icon="link"
      enabled
    />
  );

  return (
    <>
      {!isEditing && (
        <FloatingToolbar style={{ zIndex: 1000, color: 'red' }}>
          {linkEditButtons}
        </FloatingToolbar>
      )}
      <LinkInputDialog isOpen={isEditing} setIsOpen={setIsEditing} />
    </>
  );
};

const LinkInputDialog: React.FC<{
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}> = ({ isOpen, setIsOpen }) => {
  const popupRef = useRef<HTMLDivElement>(null);

  const { ref, x, y, width, height, active } = usePositioner(
    () => getPositioner('selection'),
    []
  );

  const { submitHref, href, setHref, cancelHref } = useFloatingLinkState();

  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);

  const inlineStyle: CSSProperties = useMemo(
    () => ({
      position: 'absolute',
      pointerEvents: 'none',
      left: x,
      top: y,
      width,
      height
    }),
    [x, y, width, height]
  );

  const combinedSelectionRefs: RefCallback<HTMLDivElement> = useCallback(
    (elem) => {
      setAnchorEl(elem);
      ref?.(elem);
    },
    [ref]
  );

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
        handleCancel();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleOnSubmit = () => {
    submitHref();
    setIsOpen(false);
    setHref('');
  };

  const handleCancel = () => {
    cancelHref();
    setIsOpen(false);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <>
      <div ref={combinedSelectionRefs} style={inlineStyle} />
      <Popper
        placement="bottom"
        modifiers={DEFAULT_MODIFIERS}
        open={active}
        anchorEl={anchorEl}
        style={{ zIndex: 1000 }}
        ref={popupRef}
      >
        <div className="bg-white shadow-lg border rounded p-4 w-64">
          <input
            type="text"
            value={href}
            onChange={(e) => setHref(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleOnSubmit();
              }
              if (e.key === 'Escape') {
                handleCancel();
              }
            }}
            placeholder="Type here..."
            className="w-full border rounded px-2 py-1 mb-2"
            autoFocus
          />
          <div className="flex justify-end gap-2">
            <button
              onClick={handleCancel}
              className="px-3 py-1 text-sm bg-gray-200 rounded"
            >
              Cancel
            </button>
            <button
              onClick={handleOnSubmit}
              className="px-3 py-1 text-sm bg-blue-600 text-white rounded"
            >
              Submit
            </button>
          </div>
        </div>
      </Popper>
    </>
  );
};

const DEFAULT_MODIFIERS = [
  {
    name: 'offset',
    options: {
      offset: [0, 8]
    }
  }
];
